// Password policy, shared by the server (enforcement) and the client (live
// guidance in the forms), so the two cannot drift apart.
//
// Applies only when a password is created or changed. Stored passwords are
// never re-checked and login is unaffected.
//
// The length and character-mix rules are the `password-validator` package.
// The one thing it cannot do is spot predictable passwords such as
// "Password1!" or "P@ssw0rd", which satisfy every character-mix rule, so that
// check lives here.
const PasswordValidator = require('password-validator');

// Managers can see every participant's personal data, so they get a longer
// minimum than attendees.
const MIN_LENGTH = { visitor: 10, manager: 12 };

// bcrypt silently ignores everything after 72 bytes.
const MAX_LENGTH = 25;

// Whole-password matches (lower case).
const COMMON_PASSWORDS = new Set([
  '123456',
  '1234567',
  '12345678',
  '123456789',
  '1234567890',
  '123123',
  '654321',
  '111111',
  '000000',
  '696969',
  '1q2w3e',
  '1q2w3e4r',
  '1qaz2wsx',
  'qazwsx',
  'zaq12wsx',
  'q1w2e3r4',
  'abc123',
  'abcd1234',
  'asdf1234',
  'qwer1234',
]);

// Words people build passwords around ("Password1!", "Qwerty123", "Welcome@2024").
// The check strips digits and symbols from both ends and undoes common
// look-alike substitutions (@ for a, 0 for o, ...) before comparing.
const COMMON_BASE_WORDS = new Set([
  'password',
  'passord',
  'losenord',
  'lösenord',
  'passw0rd',
  'pass',
  'pwd',
  'welcome',
  'velkommen',
  'valkommen',
  'välkommen',
  'admin',
  'administrator',
  'letmein',
  'login',
  'qwerty',
  'qwertyuiop',
  'qwertz',
  'azerty',
  'asdf',
  'asdfgh',
  'asdfghjkl',
  'zxcvbn',
  'zxcvbnm',
  'abc',
  'abcd',
  'abcdef',
  'iloveyou',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'superman',
  'batman',
  'shadow',
  'mustang',
  'freedom',
  'whatever',
  'trustno',
  'secret',
  'hello',
  'test',
  'guest',
  'user',
  'root',
  'default',
  'changeme',
  'newpassword',
  'temp',
  'temporary',
  'summer',
  'winter',
  'spring',
  'autumn',
  'sverige',
  'sweden',
  'stockholm',
  'coeo',
  'joinup',
  'conference',
  'event',
  'manager',
]);

const LOOKALIKES = {
  '@': 'a',
  $: 's',
  0: 'o',
  1: 'i',
  3: 'e',
  4: 'a',
  5: 's',
  7: 't',
};

const isCommonPassword = (password) => {
  const lower = password.toLowerCase();
  if (COMMON_PASSWORDS.has(lower)) return true;
  const core = lower
    .replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '')
    .replace(/[@$013457]/g, (ch) => LOOKALIKES[ch]);
  return COMMON_BASE_WORDS.has(core);
};

// The checklist shown in the forms, in display order. `method` is the name the
// package reports when that rule fails.
const RULES = [
  { id: 'length', method: 'min', text: (min) => `at least ${min} characters` },
  { id: 'upper', method: 'uppercase', text: () => 'an uppercase letter' },
  { id: 'lower', method: 'lowercase', text: () => 'a lowercase letter' },
  { id: 'number', method: 'digits', text: () => 'a number' },
  {
    id: 'symbol',
    method: 'symbols',
    text: () => 'a symbol, for example ! @ # $ %',
  },
];

const buildSchema = (minLength) =>
  new PasswordValidator()
    .is()
    .min(minLength)
    .is()
    .max(MAX_LENGTH)
    .has()
    .uppercase()
    .has()
    .lowercase()
    .has()
    .digits(1)
    .has()
    .symbols(1);

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const joinList = (items) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * Check a password against the policy.
 *
 * @param {string} password
 * @param {'visitor' | 'manager'} [role] which minimum length applies (default 'visitor')
 * @returns {{
 *   valid: boolean,
 *   rules: { id: string, label: string, requirement: string, ok: boolean }[],
 *   tooLong: boolean,
 *   common: boolean
 * }}
 *   rules: the checklist, each with whether it is met.
 *   tooLong: longer than 72 characters (would be cut off by bcrypt).
 *   common: a predictable password, even though the character mix is fine.
 */
exports.checkPassword = (password, role) => {
  const pw = typeof password === 'string' ? password : '';
  const minLength = MIN_LENGTH[role] || MIN_LENGTH.visitor;

  // validate(..., { list: true }) returns the names of the rules that failed.
  const failed = buildSchema(minLength).validate(pw, { list: true });

  const rules = RULES.map(({ id, method, text }) => {
    const requirement = text(minLength);
    return {
      id,
      label: capitalize(requirement),
      requirement,
      ok: !failed.includes(method),
    };
  });
  const tooLong = failed.includes('max');
  const common = pw !== '' && isCommonPassword(pw);

  return {
    valid: pw !== '' && failed.length === 0 && !common,
    rules,
    tooLong,
    common,
  };
};

/**
 * One-sentence reason a password is rejected, or null when it is acceptable.
 * Used by the server so every endpoint answers the same way.
 */
exports.getPasswordError = (password, role) => {
  const result = exports.checkPassword(password, role);
  if (result.valid) return null;
  if (!password) return 'Password is required';

  const parts = [];
  const missing = result.rules.filter((r) => !r.ok).map((r) => r.requirement);
  if (missing.length) parts.push(`Password must include ${joinList(missing)}.`);
  if (result.tooLong) parts.push(`Use at most ${MAX_LENGTH} characters.`);
  if (result.common) {
    parts.push(
      'This password is too common. Choose something less predictable.'
    );
  }
  return parts.join(' ');
};

exports.PASSWORD_MIN_LENGTH = MIN_LENGTH;
exports.PASSWORD_MAX_LENGTH = MAX_LENGTH;
